// frontend/src/features/bapb/sections/SalesSection.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import EUAutoAdjustRules, { defaultRules, sanitizePctInt } from "./EUAutoAdjustRules";

/**
 * Sales — robust comps tool (residential + commercial)
 *  - Subject seed (optionally from Cover if `packet` is passed)
 *  - CSV/XLSX import + mapping (with header synonym detection)
 *  - Column picker
 *  - Transactional adjustments:
 *      * Seller concessions ($)  → cash-equivalent
 *      * Expenditures after purchase ($)
 *      * Property rights (%) + Conditions of sale (%)
 *      * Time/market adjustment (% auto from monthly trend × months between contract/close and valuation date)
 *  - Property adjustments (like E&U, % of $/unit):
 *      * Size / Age / Quality via EUAutoAdjustRules (subject never adjusted)
 *      * Location % + Custom %
 *      * Feature lump-sum ($) — pools, ADU, view, parking, etc.
 *  - Commercial helpers:
 *      * Recurring Δ (annual $) and global Cap Rate (%) → auto capitalized lump-sum adjustment
 *  - Summary:
 *      * Median adjusted $/unit
 *      * Indicated Value (median, inclusive of lump-sum adj from each comp)
 *  - Back-compat: ingests legacy `sales.comps` from the previous component.
 */

/* ----------------- tiny helpers ----------------- */
const num = (v) => {
  if (v === null || v === undefined) return 0;
  const s = String(v).trim();
  if (!s) return 0;
  const cleaned = s.replace(/[$,%\s,]/g, "");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
};
const pctWholeToDec = (v) => {
  const s = String(v ?? "").trim().replace(/%/g, "");
  if (!s) return 0;
  const n = Number(s);
  return Number.isFinite(n) ? n / 100 : 0;
};
const fmtMoney0 = (v) =>
  v === null || v === undefined || isNaN(v) ? "—" : `$${Math.round(v).toLocaleString()}`;
const fmtMoney2 = (v) =>
  v === null || v === undefined || isNaN(v)
    ? "—"
    : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const median = (arr) => {
  const xs = arr.filter((x) => Number.isFinite(x)).slice().sort((a, b) => a - b);
  if (xs.length === 0) return 0;
  const mid = Math.floor(xs.length / 2);
  return xs.length % 2 ? xs[mid] : (xs[mid - 1] + xs[mid]) / 2;
};
const uid = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `r-${Math.random().toString(36).slice(2, 8)}`);

const todayISO = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const parseDate = (s) => {
  if (!s) return null;
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) {
    const d = new Date(s);
    return isNaN(+d) ? null : d;
  }
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return isNaN(+d) ? null : d;
};
const monthsBetween = (d1, d2) => {
  if (!d1 || !d2) return 0;
  const ms = d2.getTime() - d1.getTime();
  // average month length ~30.44 days
  return ms / (1000 * 60 * 60 * 24 * 30.44);
};

/* ----------------- CSV + optional XLSX parsing ----------------- */
function splitCsvLine(line) {
  const out = [];
  let cur = "", inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQ && line[i + 1] === '"') { cur += '"'; i++; }
      else inQ = !inQ;
    } else if (ch === "," && !inQ) {
      out.push(cur); cur = "";
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out.map((s) => s.trim());
}
function parseCsv(text) {
  const lines = String(text).replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n").filter((l) => l.trim() !== "");
  if (!lines.length) return { headers: [], rows: [] };
  const headers = splitCsvLine(lines[0] || "");
  const rows = lines.slice(1).map((ln) => splitCsvLine(ln));
  return { headers, rows };
}
async function parseXlsx(file) {
  if (!window.XLSX) throw new Error("XLSX parser not found. Make sure window.XLSX is available.");
  const buf = await file.arrayBuffer();
  const wb = window.XLSX.read(buf, { type: "array" });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const rows = window.XLSX.utils.sheet_to_json(ws, { header: 1 });
  const headers = (rows[0] || []).map((c) => String(c ?? ""));
  const out = rows.slice(1).map((r) => r.map((c) => (c == null ? "" : String(c))));
  return { headers, rows: out };
}

/* ----------------- auto-map headers ----------------- */
const FIELD_DEFS = [
  { key: "address",        label: "Address",             synonyms: ["address", "site address", "situs", "location", "street", "full address", "addr"] },
  { key: "property_type",  label: "Property Type",       synonyms: ["property type","ptype","use","type","category","group"] },
  { key: "class",          label: "Quality",             synonyms: ["quality","class","building class","grade"] },
  { key: "size",           label: "Size / Units",        synonyms: ["sf","sqft","area","gla","nra","size","units","unit count","building sf","bldg sf","net rentable"] },
  { key: "year_built",     label: "Year Built",          synonyms: ["year built","yr built","built","effective year built","eyb","year"] },
  { key: "nbhd",           label: "Neighborhood",        synonyms: ["neighborhood","nbhd","market area","district","ima","location code"] },

  { key: "contract_date",  label: "Contract Date",       synonyms: ["contract","contract date","pended","under contract","effective date"] },
  { key: "close_date",     label: "Close Date",          synonyms: ["close","closing","close date","sale date","sold date"] },
  { key: "price",          label: "Reported Price ($)",  synonyms: ["price","sale price","sold price","gross price"] },
  { key: "concessions",    label: "Seller Concessions ($)", synonyms: ["concessions","seller credit","seller concessions","points","rate buydown","incentives"] },
  { key: "expenditures",   label: "Expenditures after Purchase ($)", synonyms: ["expenditures","after purchase","fix immediate","repairs","post close costs"] },
  { key: "rights_pct",     label: "Property Rights Adj (%)", synonyms: ["rights","property rights","fee simple","leasehold","leased fee"] },
  { key: "conditions_pct", label: "Conditions of Sale Adj (%)", synonyms: ["conditions","motivation","distress","related party","relocation","reo"] },

  { key: "adj_location_pct", label: "Location Adj (%)",  synonyms: ["location adj","externalities","view","busy road","school","loc adj"] },
  { key: "adj_custom_pct",   label: "Custom Adj (%)",    synonyms: ["custom adj","misc adj","other adj","functional"] },
  { key: "adj_features_dollar", label: "Features Adj ($)", synonyms: ["features","feature adj","lump sum","pool","adu","garage","view premium"] },

  { key: "annual_delta",   label: "Recurring Δ/yr ($)",  synonyms: ["annual delta","noi delta","recurring","expense delta","rent delta","annual change"] },
  { key: "notes",          label: "Notes",               synonyms: ["notes","remarks","comment","comments"] },
];
const INDEX_BY_KEY = Object.fromEntries(FIELD_DEFS.map((f, i) => [f.key, i]));

function autoMap(headers) {
  const map = {};
  headers.forEach((raw, idx) => {
    const h = String(raw || "").toLowerCase().replace(/[^a-z0-9]/g, " ").replace(/\s+/g, " ").trim();
    let best = null, score = 0;
    for (const f of FIELD_DEFS) {
      for (const candidate of [f.label, ...(f.synonyms || [])]) {
        const c = String(candidate).toLowerCase();
        if (!c) continue;
        if (h === c) { best = f.key; score = 100; break; }
        if (h.includes(c)) { if (score < 50) { best = f.key; score = 50; } }
      }
      if (score === 100) break;
    }
    map[idx] = best || null;
  });
  return map;
}

/* ----------------- small UI bits ----------------- */
function Checkbox({ checked, onChange, disabled, title }) {
  return (
    <input
      type="checkbox"
      className="h-4 w-4 rounded border-slate-300"
      checked={!!checked}
      onChange={(e) => onChange?.(e.target.checked)}
      disabled={disabled}
      title={title}
    />
  );
}
function NumberCell({ value, onChange, suffix, className, disabled, inputMode = "text" }) {
  return (
    <div className={`flex items-center ${className || ""}`}>
      <input
        className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
        value={value ?? ""}
        onChange={(e) => onChange?.(e.target.value)}
        disabled={disabled}
        inputMode={inputMode}
      />
      {suffix && <span className="ml-1 text-xs text-slate-500">{suffix}</span>}
    </div>
  );
}

/* ----------------- mapping dialog ----------------- */
function MappingDialog({ open, headers, sampleRows, initialFieldToIndex, onCancel, onImport, unitNoun }) {
  const FIELDS_TO_SHOW = [
    "address","property_type","class","size","year_built","nbhd",
    "contract_date","close_date","price","concessions","expenditures","rights_pct","conditions_pct",
    "adj_location_pct","adj_custom_pct","adj_features_dollar","annual_delta","notes"
  ];
  const [fieldToIndex, setFieldToIndex] = useState(initialFieldToIndex || {});
  if (!open) return null;

  const first3 = sampleRows.slice(0, 3);

  return (
    <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/30">
      <div className="w-[820px] max-w-[95vw] rounded-xl border border-slate-200 bg-white shadow-lg">
        <div className="px-4 py-3 border-b">
          <div className="font-semibold">Map Columns</div>
          <div className="text-xs text-slate-500">We guessed based on your headers; tweak if needed, then Import.</div>
        </div>

        <div className="p-4 space-y-3 max-h-[70vh] overflow-auto">
          {FIELDS_TO_SHOW.map((key) => {
            const def = FIELD_DEFS[INDEX_BY_KEY[key]];
            const idx = fieldToIndex[key] ?? -1;
            return (
              <div key={key} className="grid grid-cols-5 gap-2 items-center">
                <div className="text-sm col-span-2">
                  <div className="font-medium">{def?.label}</div>
                  {key === "size" && (
                    <div className="text-xs text-slate-500">
                      Interpreted as {unitNoun === "SF" ? "SF" : unitNoun}
                    </div>
                  )}
                </div>
                <div className="col-span-3">
                  <select
                    className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
                    value={idx}
                    onChange={(e) => setFieldToIndex((p) => ({ ...p, [key]: Number(e.target.value) }))}
                  >
                    <option value={-1}>(not mapped)</option>
                    {headers.map((h, i) => (
                      <option key={i} value={i}>{h || `(column ${i + 1})`}</option>
                    ))}
                  </select>
                  {idx >= 0 && (
                    <div className="mt-1 text-[11px] text-slate-500 truncate">
                      {first3.map((r) => String(r[idx] ?? "")).filter(Boolean).slice(0,2).join(" · ") || "—"}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="px-4 py-3 border-t flex items-center justify-end gap-2">
          <button onClick={onCancel} className="rounded-lg px-3 py-1.5 border border-slate-300 bg-white">Cancel</button>
          <button onClick={() => onImport(fieldToIndex)} className="rounded-lg px-3 py-1.5 border border-sky-300 bg-sky-50 text-sky-800">
            Import
          </button>
        </div>
      </div>
    </div>
  );
}

/* ----------------- Section ----------------- */
export default function SalesSection({ packet, data, onChange, onSave }) {
  // ---------- Cover context (optional) ----------
  const coverCtx = useMemo(() => {
    let latestCover = null, maxOrder = -Infinity;
    for (const s of packet?.sections || []) {
      if (String(s?.type || "").toLowerCase() !== "cover") continue;
      const oi = s?.order_index ?? 0;
      if (oi > maxOrder) { maxOrder = oi; latestCover = s; }
    }
    const coverMeta = latestCover?.data || {};
    const pi = coverMeta?.property_info || {};
    const unitType = (pi.unit_type || "").toString().toUpperCase() || "SF"; // default SF for comps
    const sizeFromCover =
      Number(String(pi.units ?? pi.sqft ?? pi.area_sf ?? "").replace(/[$,%\s,]/g, "")) || 0;

    return {
      address: pi.address || pi.site_name || "",
      property_type: pi.property_type || "",
      class: "",
      size: sizeFromCover,
      year_built: pi.year_built || "",
      nbhd: pi.cad_location_code || "",
      unitType,
    };
  }, [packet]);

  const unitNoun = coverCtx.unitType === "SF" ? "SF" : coverCtx.unitType === "BEDS" ? "Bed" : (coverCtx.unitType || "Unit");
  const perUnitLabel = coverCtx.unitType === "SF" ? "$ / SF" : `$ / ${unitNoun}`;

  // ---------- Back-compat ingestion of legacy sales.comps ----------
  const initialLegacy = Array.isArray(data?.sales?.comps) ? data.sales.comps : [];
  const legacyToRow = (c) => ({
    id: uid(), use: true,
    address: c.address || "",
    property_type: "",
    class: "",
    size: c.building_sf || "",
    year_built: "",
    nbhd: "",
    contract_date: "",
    close_date: c.date || "",
    price: c.price || "",
    concessions: "",
    expenditures: "",
    rights_pct: "",
    conditions_pct: "",
    adj_location_pct: "",
    adj_custom_pct: "",
    adj_features_dollar: "",
    annual_delta: "",
    notes: c.notes || "",
  });

  const initial = data?.sales || {};
  const seededRowsFromProps =
    Array.isArray(initial.rows) ? initial.rows
    : initialLegacy.length ? initialLegacy.map(legacyToRow)
    : [];

  const [rows, setRows] = useState(seededRowsFromProps);
  const [rules, setRules] = useState(initial.config?.auto_rules || defaultRules());

  const DEFAULT_COLUMNS = {
    use: true,
    address: true,
    property_type: false,
    class: true,
    size: true,
    year_built: true,
    nbhd: false,

    contract_date: true,
    close_date: true,
    price: true,
    concessions: true,
    expenditures: true,
    rights_pct: false,
    conditions_pct: false,

    ce_price: true,          // computed: cash-equivalent
    txn_price: true,         // computed after expenditures/rights/conditions (pre-time)
    time_adj_pct: true,      // computed % from monthly trend × months
    price_after_txn: true,   // computed after time adj

    per_unit: true,          // base $/unit from price_after_txn / comp size

    adj_size_pct: true,
    adj_age_pct: true,
    adj_quality_pct: true,
    adj_location_pct: true,
    adj_custom_pct: true,

    adj_features_dollar: true,
    annual_delta: false,
    cap_adj_dollar: false,

    adj_per_unit: true,       // computed $/unit after property %
    indicated_from_comp: true,// computed indicated value for subject
    notes: false,
  };

  const [config, setConfig] = useState(() => ({
    subjectId: initial.config?.subjectId || null,
    columns: { ...DEFAULT_COLUMNS, ...(initial.config?.columns || {}) },
    valuationDate: initial.config?.valuationDate || todayISO(),
    monthlyTrendPct: initial.config?.monthlyTrendPct ?? "0",
    useContractDate: initial.config?.useContractDate ?? true,
    capRatePct: initial.config?.capRatePct ?? "",
  }));

  // Distinct qualities for reorder pills
  const qualityValues = useMemo(() => {
    const set = new Set(
      (rows || [])
        .map((r) => (r?.class ?? "").toString().trim())
        .filter((s) => s !== "")
    );
    return Array.from(set);
  }, [rows]);

  // Seed Subject from Cover if no subject yet
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    if (!rows || rows.length === 0) {
      const subj = {
        id: uid(),
        use: false,
        address: coverCtx.address,
        property_type: coverCtx.property_type,
        class: "",
        size: coverCtx.size || "",
        year_built: coverCtx.year_built || "",
        nbhd: coverCtx.nbhd || "",
        contract_date: "",
        close_date: "",
        price: "",
        concessions: "",
        expenditures: "",
        rights_pct: "",
        conditions_pct: "",
        adj_location_pct: "",
        adj_custom_pct: "",
        adj_features_dollar: "",
        annual_delta: "",
        notes: "",
      };
      setRows([subj]);
      setConfig((p) => ({ ...p, subjectId: subj.id }));
      seededRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(coverCtx), rows?.length]);

  const subject = useMemo(() => rows.find((r) => r.id === config.subjectId) || null, [rows, config.subjectId]);
  const comps   = useMemo(() => rows.filter((r) => r.id !== config.subjectId), [rows, config.subjectId]);
  const usedComps = useMemo(() => comps.filter((r) => !!r.use), [comps]);

  /* ----------- APPLY auto rules (size/age/quality) ----------- */
  function applyAutoAdjustments(sourceRows = rows, subjRow = subject) {
    if (!subjRow) return;
    const subjSize = num(subjRow.size);
    const subjYear = num(subjRow.year_built);
    const subjQuality = (subjRow.class || "").toString();

    const qOrder = (rules.quality?.order || []).map((q) => (q == null ? "" : String(q)));
    const qIndex = (q) => {
      const s = (q == null ? "" : String(q)).trim().toLowerCase();
      let idx = qOrder.findIndex((x) => String(x || "").trim().toLowerCase() === s);
      if (idx === -1) idx = qOrder.length; // unseen qualities treated as lowest
      return idx;
    };

    setRows((prev) => {
      const base = sourceRows === rows ? prev : sourceRows;
      return base.map((r) => {
        const isSubject = r.id === config.subjectId;
        if (isSubject) return r;

        let sizeAdj = r.adj_size_pct ?? "";
        let ageAdj  = r.adj_age_pct ?? "";
        let qualAdj = r.adj_quality_pct ?? "";

        // SIZE (percent per X units, direction settings come from EUAutoAdjustRules)
        if (rules.size?.enabled && subjSize > 0 && num(r.size) > 0 && num(rules.size.amountPer) > 0) {
          const diff = num(r.size) - subjSize; // + => comp larger
          const steps = Math.abs(diff) / num(rules.size.amountPer);
          const basePct = num(rules.size.pctPer) * steps;
          const dir = diff >= 0 ? (rules.size.largerDirection || "up") : (rules.size.smallerDirection || "down");
          sizeAdj = dir === "up" ? String(round2(basePct)) : String(-round2(basePct));
        }

        // AGE (percent per X years)
        if (rules.age?.enabled && subjYear > 0 && num(r.year_built) > 0 && num(rules.age.yearsPer) > 0) {
          const diff = num(r.year_built) - subjYear; // + => comp newer
          const steps = Math.abs(diff) / num(rules.age.yearsPer);
          const basePct = num(rules.age.pctPer) * steps;
          const dir = diff < 0 ? (rules.age.olderDirection || "up") : (rules.age.newerDirection || "down");
          ageAdj = dir === "up" ? String(round2(basePct)) : String(-round2(basePct));
        }

        // QUALITY (percent per step in ranking)
        if (rules.quality?.enabled && Array.isArray(qOrder) && qOrder.length) {
          const sIdx = qIndex(subjQuality);
          const cIdx = qIndex(r.class);
          const steps = Math.abs(cIdx - sIdx);
          if (steps === 0) {
            qualAdj = "0";
          } else {
            const basePct = num(rules.quality.pctPerStep) * steps;
            const dir = cIdx < sIdx ? (rules.quality.superiorDirection || "down")
                                    : (rules.quality.inferiorDirection || "up");
            qualAdj = dir === "up" ? String(round2(basePct)) : String(-round2(basePct));
          }
        }

        return { ...r, adj_size_pct: sizeAdj, adj_age_pct: ageAdj, adj_quality_pct: qualAdj };
      });
    });
  }
  function round2(x) { return Math.round(Number(x) * 100) / 100; }

  /* ----------- Derived calcs per row ----------- */
  const withCalcs = useMemo(() => {
    const vDate = parseDate(config.valuationDate);
    const monthlyTrend = pctWholeToDec(config.monthlyTrendPct); // decimal per month
    const capRateDec = pctWholeToDec(config.capRatePct);

    const subjSize = subject ? num(subject.size) : 0;

    return rows.map((r) => {
      const isSubject = r.id === config.subjectId;

      // Dates
      const cDate = parseDate(config.useContractDate ? (r.contract_date || r.close_date) : (r.close_date || r.contract_date));
      const months = vDate && cDate ? monthsBetween(cDate, vDate) : 0; // positive if comp is older than valuation

      const price = num(r.price);
      const concessions = num(r.concessions); // + means seller credit; we subtract to get cash-equivalent
      const expenditures = num(r.expenditures); // + means buyer spent after close; add to comp

      // Transactional pipeline (order matters)
      const cePrice = price - concessions;                     // cash-equivalent
      const txn0 = cePrice + expenditures;                     // after expenditures
      const txn1 = txn0 * (1 + pctWholeToDec(r.rights_pct));   // property rights
      const txn2 = txn1 * (1 + pctWholeToDec(r.conditions_pct)); // conditions of sale
      const timePct = months * (monthlyTrend * 100);           // whole-number %
      const priceAfterTxn = txn2 * (1 + (timePct / 100));      // time/market adj

      const size = num(r.size);
      const basePerUnit = size > 0 ? priceAfterTxn / size : 0;

      const propPctSum =
        pctWholeToDec(r.adj_size_pct) +
        pctWholeToDec(r.adj_age_pct) +
        pctWholeToDec(r.adj_quality_pct) +
        pctWholeToDec(r.adj_location_pct) +
        pctWholeToDec(r.adj_custom_pct);

      const adjPerUnit = basePerUnit * (1 + propPctSum);

      const featureLump = num(r.adj_features_dollar);
      const annualDelta = num(r.annual_delta);
      const capAdjDollar = capRateDec > 0 ? (annualDelta / capRateDec) : 0;

      const indicatedFromComp =
        (subjSize > 0 ? adjPerUnit * subjSize : 0) +
        featureLump +
        capAdjDollar;

      return {
        ...r,
        _isSubject: isSubject,
        _months_diff: months,
        _time_adj_pct: timePct,           // whole-number (%)
        _ce_price: cePrice,
        _txn_price_pre_time: txn2,
        _price_after_txn: priceAfterTxn,
        _per_unit: basePerUnit,
        _adj_per_unit: adjPerUnit,
        _cap_adj_dollar: capAdjDollar,
        _indicated_from_comp: indicatedFromComp,
      };
    });
  }, [rows, config.valuationDate, config.monthlyTrendPct, config.useContractDate, config.capRatePct, subject]);

  const stats = useMemo(() => {
    const byId = Object.fromEntries(withCalcs.map((r) => [r.id, r]));
    const adjPerUnits = usedComps.map((r) => byId[r.id]?._adj_per_unit || 0);
    const indicateds  = usedComps.map((r) => byId[r.id]?._indicated_from_comp || 0);
    const medPerUnit = median(adjPerUnits);
    const medIndicated = median(indicateds);
    return {
      count: usedComps.length,
      median_adj_per_unit: medPerUnit,
      indicated_value: medIndicated,
    };
  }, [withCalcs, usedComps]);

  /* ----------- Push up to PacketBuilder ----------- */
  const firstMount = useRef(true);
  useEffect(() => {
    if (firstMount.current) { firstMount.current = false; }
    onChange?.({
      sales: {
        rows,
        comps: rows, // back-compat key
        config: { ...config, auto_rules: rules, subjectId: config.subjectId },
        summary: {
          comps_used: stats.count,
          median_adj_per_unit: stats.median_adj_per_unit,
          indicated_value: stats.indicated_value,
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, config, rules, stats.count, stats.median_adj_per_unit, stats.indicated_value]);

  /* ----------- import (CSV + optional XLSX) ----------- */
  const [mapUI, setMapUI] = useState({ open: false, headers: [], sample: [], guessed: {} });
  const [pendingRows, setPendingRows] = useState([]);

  function toFieldToIndex(guessedHeaderIdxToField) {
    const fieldToIndex = {};
    for (const [colIdx, key] of Object.entries(guessedHeaderIdxToField || {})) {
      if (key) fieldToIndex[key] = Number(colIdx);
    }
    return fieldToIndex;
  }
  function mapRowFromFieldToIndex(cells, f2i) {
    const get = (key) => {
      const idx = f2i[key];
      return idx == null || idx < 0 ? "" : (cells[idx] ?? "");
    };
    const row = {
      id: uid(),
      use: true,
      address: get("address"),
      property_type: get("property_type"),
      class: get("class"),
      size: get("size"),
      year_built: get("year_built"),
      nbhd: get("nbhd"),
      contract_date: get("contract_date"),
      close_date: get("close_date"),
      price: get("price"),
      concessions: get("concessions"),
      expenditures: get("expenditures"),
      rights_pct: get("rights_pct"),
      conditions_pct: get("conditions_pct"),
      adj_location_pct: get("adj_location_pct"),
      adj_custom_pct: get("adj_custom_pct"),
      adj_features_dollar: get("adj_features_dollar"),
      annual_delta: get("annual_delta"),
      notes: get("notes"),
    };
    return row;
  }
  async function openFileAndPrepareMapping(file) {
    let parsed = { headers: [], rows: [] };
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (ext === "xlsx") {
      try { parsed = await parseXlsx(file); }
      catch (e) { alert(e?.message || "Failed to parse Excel. Ensure window.XLSX is available."); return; }
    } else {
      const text = await file.text();
      parsed = parseCsv(text);
    }
    const guessed = autoMap(parsed.headers);
    setMapUI({ open: true, headers: parsed.headers, sample: parsed.rows.slice(0, 10), guessed });
    setPendingRows(parsed.rows);
  }
  function confirmImport(fieldToIndex) {
    const newRows = (pendingRows || []).map((cells) => mapRowFromFieldToIndex(cells, fieldToIndex));
    if (!newRows.length) { setMapUI({ open: false, headers: [], sample: [], guessed: {} }); return; }

    // If there's no subject yet, keep current subject (if any). Otherwise ensure one exists.
    let next = rows.length ? rows.slice() : [];
    if (!next.some((r) => r.id === config.subjectId) && next.length === 0) {
      // Create a blank subject row at top
      const subj = {
        id: uid(), use: false, address: coverCtx.address || "", property_type: coverCtx.property_type || "",
        class: "", size: coverCtx.size || "", year_built: coverCtx.year_built || "", nbhd: coverCtx.nbhd || "",
        contract_date: "", close_date: "", price: "", concessions: "", expenditures: "",
        rights_pct: "", conditions_pct: "", adj_location_pct: "", adj_custom_pct: "",
        adj_features_dollar: "", annual_delta: "", notes: "",
      };
      next = [subj];
      setConfig((prev) => ({ ...prev, subjectId: subj.id }));
    }

    next = [...next, ...newRows.map((r) => ({ ...r, use: true }))];
    setRows(next);
    setMapUI({ open: false, headers: [], sample: [], guessed: {} });
    setPendingRows([]);

    // optional: apply current auto rules immediately
    applyAutoAdjustments(next, next.find((r) => r.id === config.subjectId));
  }
  function handleFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    openFileAndPrepareMapping(f);
    e.target.value = "";
  }

  /* ----------- table editing ----------- */
  const C = config.columns;
  function setRow(id, patch) { setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r))); }
  function removeRow(id) { setRows((prev) => prev.filter((r) => r.id !== id)); if (config.subjectId === id) setConfig((p) => ({ ...p, subjectId: null })); }
  function addEmptyComp() {
    const row = {
      id: uid(), use: true,
      address: "", property_type: "", class: "",
      size: "", year_built: "", nbhd: "",
      contract_date: "", close_date: "", price: "",
      concessions: "", expenditures: "", rights_pct: "", conditions_pct: "",
      adj_location_pct: "", adj_custom_pct: "",
      adj_features_dollar: "", annual_delta: "",
      notes: "",
    };
    setRows((prev) => [...prev, row]);
  }

  // Render order
  const ORDER = [
    "use","address","property_type","class","size","year_built","nbhd",
    "contract_date","close_date","price","concessions","expenditures","rights_pct","conditions_pct",
    "ce_price","txn_price","time_adj_pct","price_after_txn",
    "per_unit",
    "adj_size_pct","adj_age_pct","adj_quality_pct","adj_location_pct","adj_custom_pct",
    "adj_features_dollar","annual_delta","cap_adj_dollar",
    "adj_per_unit","indicated_from_comp","notes"
  ];

  /* ----------------- UI ----------------- */
  return (
    <div className="space-y-4">
      {/* Top actions */}
      <div className="flex flex-wrap items-center gap-2">
        <label className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm cursor-pointer hover:border-slate-400">
          <input
            type="file"
            accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            onChange={handleFile}
          />
          Import CSV / Excel
        </label>
        <button
          onClick={addEmptyComp}
          className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm hover:border-slate-400"
        >
          + Add Comp
        </button>

        {/* column picker */}
        <div className="relative ml-auto">
          <button
            onClick={() => setConfig((p) => ({ ...p, _showPicker: !p._showPicker }))}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm hover:border-slate-400"
          >
            Columns
          </button>
          {config._showPicker && (
            <div className="absolute right-0 z-10 mt-1 w-96 rounded-lg border border-slate-200 bg-white p-3 shadow">
              <div className="text-xs text-slate-500 mb-2">Show in grid</div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
                {ORDER.map((k) => (
                  <label key={k} className="flex items-center gap-2">
                    <Checkbox
                      checked={!!C[k]}
                      onChange={(v) => setConfig((prev) => ({ ...prev, columns: { ...prev.columns, [k]: v } }))}
                    />
                    <span>
                      {k === "per_unit" ? perUnitLabel :
                       k === "adj_per_unit" ? `Adj ${perUnitLabel}` :
                       k === "ce_price" ? "Cash-Equivalent Price" :
                       k === "txn_price" ? "Txn Price (pre-Time)" :
                       k === "time_adj_pct" ? "Time Adj%" :
                       k === "price_after_txn" ? "Price After Time" :
                       k === "nbhd" ? "Neighborhood" :
                       k === "size" ? `Size (${unitNoun})` :
                       k === "adj_features_dollar" ? "Features Adj ($)" :
                       k === "cap_adj_dollar" ? "Cap Adj ($)" :
                       k === "indicated_from_comp" ? "Indicated Value (this Comp)" :
                       k.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase())}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mapping dialog */}
      <MappingDialog
        open={mapUI.open}
        headers={mapUI.headers}
        sampleRows={mapUI.sample}
        unitNoun={unitNoun}
        initialFieldToIndex={toFieldToIndex(mapUI.guessed)}
        onCancel={() => setMapUI({ open: false, headers: [], sample: [], guessed: {} })}
        onImport={confirmImport}
      />

      {/* Assumptions & Summary */}
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-slate-500">Valuation Date:</span>
          <input
            type="date"
            className="rounded border border-slate-300 px-2 py-1"
            value={config.valuationDate}
            onChange={(e) => setConfig((p) => ({ ...p, valuationDate: e.target.value }))}
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-slate-500">Market Trend:</span>
          <input
            className="w-20 rounded border border-slate-300 px-2 py-1"
            value={config.monthlyTrendPct}
            onChange={(e) => setConfig((p) => ({ ...p, monthlyTrendPct: sanitizePctInt(e.target.value) }))}
            inputMode="decimal"
            placeholder="e.g., 0.5"
          />
          <span className="text-slate-500 text-xs">%/mo</span>
          <label className="ml-2 text-xs">
            <input
              type="checkbox"
              className="mr-1 align-middle"
              checked={!!config.useContractDate}
              onChange={(e) => setConfig((p) => ({ ...p, useContractDate: e.target.checked }))}
            />
            Use Contract Date when available
          </label>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-slate-500">Cap Rate (optional):</span>
          <input
            className="w-20 rounded border border-slate-300 px-2 py-1"
            value={config.capRatePct}
            onChange={(e) => setConfig((p) => ({ ...p, capRatePct: sanitizePctInt(e.target.value) }))}
            inputMode="decimal"
            placeholder="e.g., 6"
          />
          <span className="text-slate-500 text-xs">%</span>
        </div>

        <div className="ml-auto flex items-center gap-6">
          <div><span className="text-slate-500">Comps used:</span> <span className="font-medium">{stats.count}</span></div>
          <div><span className="text-slate-500">Median Adj {perUnitLabel}:</span> <span className="font-medium">{fmtMoney2(stats.median_adj_per_unit)}</span></div>
          <div><span className="text-slate-500">Indicated Value (median):</span> <span className="font-semibold">{fmtMoney0(stats.indicated_value)}</span></div>
        </div>
      </div>

      {/* Auto rules (size/age/quality) */}
      <EUAutoAdjustRules
        rules={rules}
        onChange={setRules}
        unitLabel={unitNoun === "SF" ? "SF" : unitNoun}
        qualities={qualityValues}
        onApply={() => applyAutoAdjustments()}
      />

      {/* Table */}
      <div className="overflow-auto rounded-xl border border-slate-200">
        <table className="min-w-[2200px] w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              {/* role is always first + sticky */}
              <th className="px-2 py-2 text-left sticky left-0 bg-slate-50 z-10 w-[140px]">Role</th>
              {C.use && <th className="px-2 py-2 text-left sticky left-[140px] bg-slate-50 z-10">Use</th>}

              {C.address        && <th className="px-2 py-2 text-left">Address</th>}
              {C.property_type  && <th className="px-2 py-2 text-left">Property Type</th>}
              {C.class          && <th className="px-2 py-2 text-left">Quality</th>}
              {C.size           && <th className="px-2 py-2 text-right">Size ({unitNoun})</th>}
              {C.year_built     && <th className="px-2 py-2 text-right">Year Built</th>}
              {C.nbhd           && <th className="px-2 py-2 text-left">Neighborhood</th>}

              {C.contract_date  && <th className="px-2 py-2 text-right">Contract Date</th>}
              {C.close_date     && <th className="px-2 py-2 text-right">Close Date</th>}
              {C.price          && <th className="px-2 py-2 text-right">Reported Price</th>}
              {C.concessions    && <th className="px-2 py-2 text-right">Seller Concessions ($)</th>}
              {C.expenditures   && <th className="px-2 py-2 text-right">Post‑Purchase Exp ($)</th>}
              {C.rights_pct     && <th className="px-2 py-2 text-right">Rights Adj%</th>}
              {C.conditions_pct && <th className="px-2 py-2 text-right">Conditions Adj%</th>}

              {C.ce_price       && <th className="px-2 py-2 text-right">Cash‑Eq Price</th>}
              {C.txn_price      && <th className="px-2 py-2 text-right">Txn Price (pre‑Time)</th>}
              {C.time_adj_pct   && <th className="px-2 py-2 text-right">Time Adj%</th>}
              {C.price_after_txn&& <th className="px-2 py-2 text-right">Price After Time</th>}

              {C.per_unit       && <th className="px-2 py-2 text-right">{perUnitLabel}</th>}

              {C.adj_size_pct   && <th className="px-2 py-2 text-right">Size Adj%</th>}
              {C.adj_age_pct    && <th className="px-2 py-2 text-right">Age Adj%</th>}
              {C.adj_quality_pct&& <th className="px-2 py-2 text-right">Quality Adj%</th>}
              {C.adj_location_pct&&<th className="px-2 py-2 text-right">Location Adj%</th>}
              {C.adj_custom_pct && <th className="px-2 py-2 text-right">Custom Adj%</th>}

              {C.adj_features_dollar && <th className="px-2 py-2 text-right">Features Adj ($)</th>}
              {C.annual_delta   && <th className="px-2 py-2 text-right">Recurring Δ/yr ($)</th>}
              {C.cap_adj_dollar && <th className="px-2 py-2 text-right">Cap Adj ($)</th>}

              {C.adj_per_unit   && <th className="px-2 py-2 text-right">Adj {perUnitLabel}</th>}
              {C.indicated_from_comp && <th className="px-2 py-2 text-right">Indicated (this Comp)</th>}

              {C.notes          && <th className="px-2 py-2 text-left">Notes</th>}
              <th className="px-2 py-2"></th>
            </tr>
          </thead>

          <tbody>
            {withCalcs.map((r) => {
              const lockSize = r._isSubject || !!rules.size?.enabled;
              const lockAge  = r._isSubject || !!rules.age?.enabled;
              const lockQual = r._isSubject || !!rules.quality?.enabled;
              return (
                <tr key={r.id} className="border-t">
                  {/* role */}
                  <td className="px-2 py-1 sticky left-0 bg-white z-10 w-[140px]">
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="radio"
                        name="subject"
                        checked={r._isSubject}
                        onChange={() => setConfig((p) => ({ ...p, subjectId: r.id }))}
                        title="Set as Subject (Subject is never adjusted)"
                      />
                      <span className={r._isSubject ? "font-medium" : ""}>{r._isSubject ? "Subject" : "Comp"}</span>
                    </label>
                  </td>

                  {C.use && (
                    <td className="px-2 py-1 sticky left-[140px] bg-white z-10">
                      <Checkbox checked={!!r.use} onChange={(v) => setRow(r.id, { use: v })} disabled={r._isSubject} />
                    </td>
                  )}

                  {C.address && (
                    <td className="px-2 py-1">
                      <input className="w-full rounded border border-slate-300 px-2 py-1" value={r.address || ""} onChange={(e) => setRow(r.id, { address: e.target.value })} />
                    </td>
                  )}
                  {C.property_type && (
                    <td className="px-2 py-1">
                      <input className="w-full rounded border border-slate-300 px-2 py-1" value={r.property_type || ""} onChange={(e) => setRow(r.id, { property_type: e.target.value })} />
                    </td>
                  )}
                  {C.class && (
                    <td className="px-2 py-1">
                      <input className="w-full rounded border border-slate-300 px-2 py-1" value={r.class || ""} onChange={(e) => setRow(r.id, { class: e.target.value })} placeholder="e.g., A, B, C or Renovated, Avg, Dated" />
                    </td>
                  )}
                  {C.size && (
                    <td className="px-2 py-1 text-right">
                      <NumberCell value={r.size} onChange={(v) => setRow(r.id, { size: v.replace(/[^\d.]/g, "") })} inputMode="decimal" />
                    </td>
                  )}
                  {C.year_built && (
                    <td className="px-2 py-1 text-right">
                      <NumberCell value={r.year_built} onChange={(v) => setRow(r.id, { year_built: v.replace(/[^\d]/g, "") })} inputMode="numeric" />
                    </td>
                  )}
                  {C.nbhd && (
                    <td className="px-2 py-1">
                      <input className="w-full rounded border border-slate-300 px-2 py-1" value={r.nbhd || ""} onChange={(e) => setRow(r.id, { nbhd: e.target.value })} />
                    </td>
                  )}

                  {C.contract_date && (
                    <td className="px-2 py-1 text-right">
                      <input type="date" className="rounded border border-slate-300 px-2 py-1" value={r.contract_date || ""} onChange={(e) => setRow(r.id, { contract_date: e.target.value })} />
                    </td>
                  )}
                  {C.close_date && (
                    <td className="px-2 py-1 text-right">
                      <input type="date" className="rounded border border-slate-300 px-2 py-1" value={r.close_date || ""} onChange={(e) => setRow(r.id, { close_date: e.target.value })} />
                    </td>
                  )}
                  {C.price && (
                    <td className="px-2 py-1 text-right">
                      <NumberCell value={r.price} onChange={(v) => setRow(r.id, { price: v.replace(/[^\d.-]/g, "") })} inputMode="decimal" />
                    </td>
                  )}
                  {C.concessions && (
                    <td className="px-2 py-1 text-right" title="Positive number means seller paid credit to buyer (subtract to get cash‑equivalent)">
                      <NumberCell value={r.concessions} onChange={(v) => setRow(r.id, { concessions: v.replace(/[^\d.-]/g, "") })} inputMode="decimal" />
                    </td>
                  )}
                  {C.expenditures && (
                    <td className="px-2 py-1 text-right" title="Immediate post‑close expenditures needed to attain typical condition (add)">
                      <NumberCell value={r.expenditures} onChange={(v) => setRow(r.id, { expenditures: v.replace(/[^\d.-]/g, "") })} inputMode="decimal" />
                    </td>
                  )}
                  {C.rights_pct && (
                    <td className="px-2 py-1 text-right" title="Leasehold/inferior rights → positive; superior → negative">
                      <NumberCell value={r.rights_pct} onChange={(v) => setRow(r.id, { rights_pct: sanitizePctInt(v) })} suffix="%" inputMode="decimal" />
                    </td>
                  )}
                  {C.conditions_pct && (
                    <td className="px-2 py-1 text-right" title="Atypical motivation → indicate sign based on market effect">
                      <NumberCell value={r.conditions_pct} onChange={(v) => setRow(r.id, { conditions_pct: sanitizePctInt(v) })} suffix="%" inputMode="decimal" />
                    </td>
                  )}

                  {C.ce_price && <td className="px-2 py-1 text-right">{fmtMoney0(r._ce_price)}</td>}
                  {C.txn_price && <td className="px-2 py-1 text-right">{fmtMoney0(r._txn_price_pre_time)}</td>}
                  {C.time_adj_pct && (
                    <td className="px-2 py-1 text-right" title={`${r._months_diff?.toFixed?.(2)} mo × ${config.monthlyTrendPct || 0}%/mo`}>
                      {`${Number(r._time_adj_pct || 0).toFixed(2)}%`}
                    </td>
                  )}
                  {C.price_after_txn && <td className="px-2 py-1 text-right font-medium">{fmtMoney0(r._price_after_txn)}</td>}

                  {C.per_unit && <td className="px-2 py-1 text-right">{fmtMoney2(r._per_unit)}</td>}

                  {C.adj_size_pct && (
                    <td className="px-2 py-1 text-right">
                      <NumberCell value={r.adj_size_pct} onChange={(v) => !lockSize && setRow(r.id, { adj_size_pct: sanitizePctInt(v) })} suffix="%" disabled={lockSize} inputMode="decimal" />
                    </td>
                  )}
                  {C.adj_age_pct && (
                    <td className="px-2 py-1 text-right">
                      <NumberCell value={r.adj_age_pct} onChange={(v) => !lockAge && setRow(r.id, { adj_age_pct: sanitizePctInt(v) })} suffix="%" disabled={lockAge} inputMode="decimal" />
                    </td>
                  )}
                  {C.adj_quality_pct && (
                    <td className="px-2 py-1 text-right">
                      <NumberCell value={r.adj_quality_pct} onChange={(v) => !lockQual && setRow(r.id, { adj_quality_pct: sanitizePctInt(v) })} suffix="%" disabled={lockQual} inputMode="decimal" />
                    </td>
                  )}
                  {C.adj_location_pct && (
                    <td className="px-2 py-1 text-right">
                      <NumberCell value={r.adj_location_pct} onChange={(v) => setRow(r.id, { adj_location_pct: sanitizePctInt(v) })} suffix="%" inputMode="decimal" />
                    </td>
                  )}
                  {C.adj_custom_pct && (
                    <td className="px-2 py-1 text-right">
                      <NumberCell value={r.adj_custom_pct} onChange={(v) => setRow(r.id, { adj_custom_pct: sanitizePctInt(v) })} suffix="%" inputMode="decimal" />
                    </td>
                  )}

                  {C.adj_features_dollar && (
                    <td className="px-2 py-1 text-right" title="Lump‑sum feature/functional/view adjustments for this comp">
                      <NumberCell value={r.adj_features_dollar} onChange={(v) => setRow(r.id, { adj_features_dollar: v.replace(/[^\d.-]/g, "") })} inputMode="decimal" />
                    </td>
                  )}
                  {C.annual_delta && (
                    <td className="px-2 py-1 text-right" title="Annual recurring Δ (e.g., NOI diff, HOA dues effect, expense delta)">
                      <NumberCell value={r.annual_delta} onChange={(v) => setRow(r.id, { annual_delta: v.replace(/[^\d.-]/g, "") })} inputMode="decimal" />
                    </td>
                  )}
                  {C.cap_adj_dollar && <td className="px-2 py-1 text-right">{fmtMoney0(r._cap_adj_dollar)}</td>}

                  {C.adj_per_unit && <td className="px-2 py-1 text-right font-medium">{fmtMoney2(r._adj_per_unit)}</td>}
                  {C.indicated_from_comp && <td className="px-2 py-1 text-right font-semibold">{fmtMoney0(r._indicated_from_comp)}</td>}

                  {C.notes && (
                    <td className="px-2 py-1">
                      <input className="w-full rounded border border-slate-300 px-2 py-1" value={r.notes || ""} onChange={(e) => setRow(r.id, { notes: e.target.value })} />
                    </td>
                  )}

                  <td className="px-2 py-1">
                    {!r._isSubject && (
                      <button
                        onClick={() => removeRow(r.id)}
                        className="rounded-md border border-rose-300 bg-rose-50 px-2 py-1 text-rose-700 hover:border-rose-400"
                        title="Remove comp"
                      >
                        ×
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Actions footer */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={addEmptyComp}
          className="rounded-md px-3 py-2 border border-slate-200 bg-white hover:border-slate-300"
        >
          + Add Comp
        </button>

        <div className="ml-auto text-xs text-slate-600">
          Percent entries are whole numbers (e.g., <b>5</b> = 5%). Subject is never adjusted. Time adjustment uses Contract Date when available.
        </div>
      </div>
    </div>
  );
}
